/**
 * RunHistory — agent run history (`GET /api/agents/runs`).
 *
 * Shows each recorded run newest-first: the agent, the acting persona, the
 * status, and the entitlement-relevant step counters. Because runs carry the
 * acting persona's groups, this view makes the "same agent, different persona,
 * different result" story auditable at a glance.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { RunHistoryResponse } from '../lib/agents';

const STATUS_BADGE: Record<string, string> = {
  running: 'bg-sky-100 text-sky-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-steel-100 text-steel-600',
  pending: 'bg-amber-100 text-amber-700',
};

export function RunHistory(): ReactNode {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['agent-runs'],
    queryFn: () => api.get<RunHistoryResponse>('/agents/runs'),
  });

  return (
    <div className="animate-fade-in" data-testid="run-history">
      <h1 className="text-xl font-semibold text-navy-900">Run History</h1>
      <p className="mt-1 text-sm text-steel-600">
        Every agent run, with the acting persona and entitlement counters.
      </p>

      {isLoading && <p className="mt-6 text-sm text-steel-500">Loading runs…</p>}

      {isError && (
        <p className="mt-6 text-sm text-rose-600" data-testid="run-history-error">
          {error instanceof Error ? error.message : 'Failed to load run history'}
        </p>
      )}

      {data && data.runs.length === 0 && (
        <p className="mt-6 text-sm text-steel-500">No runs recorded yet.</p>
      )}

      {data && data.runs.length > 0 && (
        <div className="mt-6 overflow-x-auto rounded-lg border border-steel-200">
          <table className="w-full text-sm">
            <thead className="bg-steel-50 text-left text-[12px] uppercase tracking-wide text-steel-500">
              <tr>
                <th className="px-4 py-2 font-medium">Agent</th>
                <th className="px-4 py-2 font-medium">Actor</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium text-right">Completed</th>
                <th className="px-4 py-2 font-medium text-right">Failed</th>
                <th className="px-4 py-2 font-medium">Started</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-steel-100">
              {data.runs.map((run) => (
                <tr key={run.run_id} data-testid="run-row" className="hover:bg-steel-50">
                  <td className="px-4 py-2 text-navy-900">{run.agent_name}</td>
                  <td className="px-4 py-2 text-steel-700">{run.actor}</td>
                  <td className="px-4 py-2">
                    <span
                      data-testid="run-status"
                      className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                        STATUS_BADGE[run.status] ?? 'bg-steel-100 text-steel-600'
                      }`}
                    >
                      {run.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-steel-700">
                    {run.steps_completed}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-steel-700">
                    {run.steps_failed}
                  </td>
                  <td className="px-4 py-2 text-steel-500">
                    {formatDateTime(run.started_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Format an ISO timestamp as a short local date-time (falls back to raw). */
function formatDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default RunHistory;
